import { useOktaAuth } from '@okta/okta-react';
import { useLocation } from '@reach/router';
import { intervalToDuration } from 'date-fns';
import React, {
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIdleTimer } from 'react-idle-timer';

import { Button, Modal } from '@/components';
import { createStorage } from '@/utils/storage';
import { WarningIcon } from './icons';

type IdleState = 'Active' | 'Idle' | 'Prompted';
type GetTimeoutProps = { timeout: number; promptBeforeIdle: number };

const redirectUrlStorage = createStorage('localStorage', 'pendingRedirectUrl');
const idleTimeoutStorage = createStorage('localStorage', 'idleTimeout');
const idlePromptStorage = createStorage('localStorage', 'idlePromptTimeout');

const TIMEOUT_MS = 3_600_000; // 1 hour - Total idle time before auto sign out
const PROMPT_TIMEOUT_MS = 300_000; // 5 minutes - Duration of warning prompt period

const getTimeout = (): GetTimeoutProps => {
  const storedTimeout = idleTimeoutStorage.get();
  const storedPromptTimeout = idlePromptStorage.get();

  let timeout = TIMEOUT_MS;
  let promptBeforeIdle = PROMPT_TIMEOUT_MS;

  // Only use stored values if they are less than or equal to the default values
  if (storedTimeout) {
    const parsedTimeout = parseInt(storedTimeout, 10);
    if (
      !isNaN(parsedTimeout) &&
      parsedTimeout <= TIMEOUT_MS &&
      parsedTimeout > 0
    ) {
      timeout = parsedTimeout;
    }
  }

  if (storedPromptTimeout) {
    const parsedPromptTimeout = parseInt(storedPromptTimeout, 10);
    if (
      !isNaN(parsedPromptTimeout) &&
      parsedPromptTimeout <= PROMPT_TIMEOUT_MS &&
      parsedPromptTimeout > 0
    ) {
      promptBeforeIdle = parsedPromptTimeout;
    }
  }

  return {
    timeout,
    promptBeforeIdle,
  };
};

// Format remaining time for display using date-fns
const formatTime = (milliseconds: number) => {
  const duration = intervalToDuration({ start: 0, end: milliseconds });
  const minutes = duration.minutes || 0;
  const seconds = duration.seconds || 0;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const SecureRoute: FC<{ children: ReactNode }> = ({ children }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const { timeout, promptBeforeIdle } = getTimeout();
  const [state, setState] = useState<IdleState>('Active');
  const [remaining, setRemaining] = useState<number>(timeout);
  const [open, setOpen] = useState<boolean>(false);
  const pendingLogin = useRef(false);
  const uri = useLocation();
  const storedRedirectUrl = redirectUrlStorage.get();

  const onIdle = () => {
    setState('Idle');
    setOpen(true);
    oktaAuth.signOut();
  };

  const onActive = () => {
    setState('Active');
    setOpen(false);
  };

  const onPrompt = () => {
    setState('Prompted');
    setOpen(true);
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 1000,
    crossTab: true,
  });

  const handleSignOut = () => {
    redirectUrlStorage.set(uri.pathname);
    oktaAuth.setOriginalUri(uri.pathname);
    oktaAuth.signOut();
  };

  // Update remaining time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(getRemainingTime());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime]);

  // Handle login when authState changes
  useEffect(() => {
    const login = async () => {
      await oktaAuth.signInWithRedirect();
    };

    if (!authState) {
      pendingLogin.current = false;
      return;
    }

    if (!authState.isAuthenticated && !pendingLogin.current) {
      pendingLogin.current = true;

      if (storedRedirectUrl) {
        oktaAuth.setOriginalUri(storedRedirectUrl);
      } else {
        oktaAuth.setOriginalUri(uri.pathname);
      }

      login();
    }
  }, [authState, oktaAuth, uri.pathname, storedRedirectUrl]);

  return (
    <>
      {children as ReactElement}
      <Modal
        isOpen={open}
        onRequestClose={() => {}}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={false}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-auto shadow-xl">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900 mb-4">
              <WarningIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-300" />
            </div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-2">
              Session Timeout Warning
            </h3>
            <div className="mb-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                {state === 'Prompted'
                  ? 'You will be signed out due to inactivity.'
                  : 'You have been signed out due to inactivity.'}
              </p>
              {state === 'Prompted' && (
                <p className="text-lg font-semibold text-red-600 dark:text-red-400">
                  Time remaining: {formatTime(remaining)}
                </p>
              )}
            </div>
            <div className="flex justify-center space-x-4">
              {state === 'Prompted' ? (
                <>
                  <Button
                    variant="primary"
                    onClick={activate}
                    className="flex-1"
                  >
                    I'm still here
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleSignOut}
                    className="flex-1"
                  >
                    Sign out
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSignOut}
                  className="w-full"
                >
                  Sign In Again
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export const Secure: FC<{ children: ReactNode }> = ({ children }) => {
  if (typeof window === 'undefined') {
    return null;
  }
  return <SecureRoute>{children}</SecureRoute>;
};
